import { useState, memo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { endOfWeek, endOfMonth } from "date-fns";
import { Card } from "@/components/ui/card";
import { formatInCentralTime } from "@/utils/timezone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorCard } from "@/components/ui/QueryErrorCard";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRefreshState } from "@/contexts/RefreshContext";
import { useGuest } from "@/contexts/GuestContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ScheduleSheet } from "./ScheduleSheet";

interface MyEventsThisWeekProps {
  userId?: string;
}

interface MyEvent {
  id: string;
  title: string;
  date: string;
  category: string;
  location: string;
  duration_min?: number | null;
}

interface MyEventCardProps {
  event: MyEvent;
  onNavigate: (eventId: string) => void;
}

const MyEventCard = memo(function MyEventCard({ event, onNavigate }: MyEventCardProps) {
  // Use Central Time for display
  const dayName = formatInCentralTime(event.date, 'EEE');
  const dayNumber = formatInCentralTime(event.date, 'd');
  const time = formatInCentralTime(event.date, 'h:mm a');

  return (
    <Card
      onClick={() => onNavigate(event.id)}
      className="p-4 hover:shadow-md transition-shadow cursor-pointer tap-scale"
    >
      <div className="flex items-start gap-4">
        {/* Date Badge */}
        <div className="flex-shrink-0 w-14 h-14 bg-gradient-rose-sky rounded-xl flex flex-col items-center justify-center">
          <span className="text-xs font-semibold text-foreground/80">{dayName}</span>
          <span className="font-display text-lg font-semibold text-foreground">{dayNumber}</span>
        </div>

        {/* Event Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-semibold text-foreground text-sm line-clamp-1">
              {event.title}
            </h3>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {event.category}
            </Badge>
          </div>

          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{time}</span>
              {event.duration_min && (
                <span className="text-muted-foreground/60">
                  · {event.duration_min} min
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          </div>
        </div>

        {/* View Arrow */}
        <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-2" />
      </div>
    </Card>
  );
});

export const MyEventsThisWeek = memo(function MyEventsThisWeek({ userId }: MyEventsThisWeekProps) {
  const navigate = useNavigate();
  useRefreshState(); // Keep hook for potential future use
  const { isGuest } = useGuest();
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [scheduleSheetOpen, setScheduleSheetOpen] = useState(false);

  // Don't show anything for guests
  if (isGuest) return null;

  const { data: myEvents = [], isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ['my-events-schedule', userId, viewMode],
    queryFn: async () => {
      if (!userId) return [];
      
      const now = new Date();
      const endDate = viewMode === 'month' ? endOfMonth(now) : endOfWeek(now);
      
      const { data, error } = await supabase
        .from('event_attendances')
        .select('*, events(*)')
        .eq('user_id', userId)
        .in('status', ['rsvp', 'attended'])
        .gte('events.date', now.toISOString())
        .lte('events.date', endDate.toISOString())
        .order('events(date)', { ascending: true });
      
      if (error) throw error;
      
      return data?.filter(a => a.events).map(a => a.events) || [];
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  const handleNavigate = useCallback((eventId: string) => {
    navigate(`/events/${eventId}`);
  }, [navigate]);

  // Show toast on error
  const handleRetry = useCallback(() => {
    toast.info("Retrying to load your events...");
    refetch();
  }, [refetch]);

  if (isError) {
    return (
      <QueryErrorCard
        title="Unable to load events"
        description="We had trouble fetching your events. Please try again."
        onRetry={handleRetry}
        isRetrying={isRefetching}
      />
    );
  }

  // Show loading state when userId is not yet available (auth loading)
  if (isLoading || !userId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start gap-4">
                <Skeleton className="w-14 h-14 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const hasEvents = myEvents && myEvents.length > 0;

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-lg font-semibold text-foreground">
        Your Events 📅
      </h2>
      <div className="flex items-center gap-2">
        <div className="flex bg-muted rounded-lg p-0.5">
          <button 
            onClick={() => setViewMode('month')}
            className={cn(
              "px-3 py-1 text-xs rounded-md transition-colors",
              viewMode === 'month' 
                ? "bg-background shadow-sm font-medium text-foreground" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Month
          </button>
          <button 
            onClick={() => setViewMode('week')}
            className={cn(
              "px-3 py-1 text-xs rounded-md transition-colors",
              viewMode === 'week' 
                ? "bg-background shadow-sm font-medium text-foreground" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Week
          </button>
        </div>
      </div>
    </div>
  );

  if (!hasEvents) {
    return (
      <div>
        {renderHeader()}
        <Card className="p-8 text-center bg-muted/50 border-border">
          <div className="w-14 h-14 rounded-full bg-card mx-auto mb-4 flex items-center justify-center shadow-soft">
            <Calendar className="w-6 h-6 text-secondary-foreground/70" />
          </div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-2">
            No upcoming events this {viewMode === 'month' ? 'month' : 'week'}
          </h3>

          <p className="text-sm text-muted-foreground mb-4">
            Let's change that! Browse events and RSVP to join the community.
          </p>
          <Button onClick={() => navigate('/events')} className="gap-2">
            Browse Events
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {renderHeader()}

      <div className="space-y-3">
        {myEvents.map((event: any) => (
          <MyEventCard 
            key={event.id} 
            event={event} 
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {myEvents.length > 0 && (
        <Button 
          onClick={() => setScheduleSheetOpen(true)}
          variant="outline"
          className="w-full mt-3 gap-2"
        >
          View Full Schedule
          <ArrowRight className="w-4 h-4" />
        </Button>
      )}

      <ScheduleSheet 
        open={scheduleSheetOpen} 
        onOpenChange={setScheduleSheetOpen} 
      />
    </div>
  );
});
